import { format, isValid } from 'date-fns';

import {
  DATE_DISPLAY_FORMAT,
  DATE_TIME_DISPLAY_FORMAT,
  DATE_DISPLAY_FORMAT_ALT,
  DATE_KEY_FORMAT,
  TIME_DISPLAY_FORMAT,
  TIME_KEY_FORMAT
} from 'catering/constants/dates';

export function getDateTimeFromFulfillmentDateAndTime(
  fulfillmentDate,
  fulfillmentTime
) {
  let baseDate = new Date();
  if (fulfillmentDate && fulfillmentTime) {
    baseDate = new Date(`${fulfillmentDate}T${fulfillmentTime}Z`);
  }
  return new Date(baseDate.getTime() + baseDate.getTimezoneOffset() * 60000);
}

/**
 * getTimeRangeFromFulfillmentDateTimeRange takes an array containing two string representations
 * of a local datetime object in the following format: 2021-08-12T16:00:00-04:00:00 and takes those two
 * strings and construct a Date object for each of them. With each object, we then construct
 * a formatted time display string and return each one in an object.
 *
 * Example:
 * Input: getTimeRangeFromFulfillmentDateTimeRange(['2021-08-12T16:00:00-04:00:00', '2021-08-12T16:15:00-04::00'])
 * Returns: { fulfillmentTimeStart: '04:00pm', fulfillmentTimeEnd: '04:15pm' }
 *
 * @param {string[]} fulfillmentDateTimeRange
 * @returns {
 *  fulfillmentTimeStart: string,
 *  fulfillmentTimeEnd: string
 * }
 */
export function getTimeRangeFromFulfillmentDateTimeRange(
  fulfillmentDateTimeRange
) {
  if (!fulfillmentDateTimeRange) {
    return null;
  }

  const [
    fulfillmentDateTimeStart,
    fulfillmentDateTimeEnd
  ] = fulfillmentDateTimeRange;
  const startDateTime = new Date(fulfillmentDateTimeStart);
  const endDateTime = new Date(fulfillmentDateTimeEnd);

  return {
    fulfillmentTimeStart: getFulfillmentTimeDisplay(startDateTime),
    fulfillmentTimeEnd: getFulfillmentTimeDisplay(endDateTime)
  };
}

/**
 * getFormattedFulfillmentTimeRange takes an array containing two string representations
 * of a local datetime object in the following format: 2021-08-12T16:00:00-04:00:00 and concatenates
 * the time range we evaluate from getTimeRangeFromFulfillmentDateTimeRange.
 *
 * Example:
 * Input: getFormattedFulfillmentTimeRange(['2021-08-12T16:00:00-04:00:00', '2021-08-12T16:15:00-04::00'])
 * Returns: '04:00pm - 04:15pm'
 *
 * @param {string[]} fulfillmentDateTimeRange
 * @returns {string}
 */
export function getFormattedFulfillmentTimeRange(fulfillmentDateTimeRange) {
  if (!fulfillmentDateTimeRange) {
    return null;
  }

  const {
    fulfillmentTimeStart,
    fulfillmentTimeEnd
  } = getTimeRangeFromFulfillmentDateTimeRange(fulfillmentDateTimeRange);
  return `${fulfillmentTimeStart} - ${fulfillmentTimeEnd}`;
}

/**
 * getFormattedFulfillmentDateTimeRange takes the fulfillmentDate as string, fulfillmentTime as a string,
 * and an array containing two string representations
 * of a local datetime object in the following format: 2021-08-12T16:00:00-04:00:00 and concatenates
 * the formattedDate and the time range string we evaluate from getFormattedFulfillmentDateTimeRange.
 *
 * Example:
 * Input: getFormattedFulfillmentDateTimeRange('2021-08-12', '16:00:00', ['2021-08-12T16:00:00-04:00:00', '2021-08-12T16:15:00-04::00'])
 * Returns: 'Thu Aug 12 04:00pm - 04:15pm'
 *
 * @param {string} fulfillmentDate
 * @param {string} fulfillmentTime
 * @param {string[]} fulfillmentDateTimeRange
 * @returns {string}
 */
export function getFormattedFulfillmentDateTimeRange(
  fulfillmentDate,
  fulfillmentTime,
  fulfillmentDateTimeRange
) {
  if (!fulfillmentDateTimeRange || !fulfillmentDate || !fulfillmentTime) {
    return null;
  }

  const formattedFulfillmentTime = getFormattedFulfillmentTimeRange(
    fulfillmentDateTimeRange
  );
  const formattedDate = format(
    getDateTimeFromFulfillmentDateAndTime(fulfillmentDate, fulfillmentTime),
    DATE_DISPLAY_FORMAT_ALT
  );

  return `${formattedDate} ${formattedFulfillmentTime}`;
}

export function getFormattedFulfillmentDateTime(
  fulfillmentDate,
  fulfillmentTime
) {
  return format(
    getDateTimeFromFulfillmentDateAndTime(fulfillmentDate, fulfillmentTime),
    DATE_TIME_DISPLAY_FORMAT
  );
}

export function getFulfillmentDateKey(fulfillmentDate) {
  if (isValid(fulfillmentDate)) {
    return format(fulfillmentDate, DATE_KEY_FORMAT);
  } else {
    return 'Invalid Format';
  }
}

export function getFulfillmentDateDisplay(
  fulfillmentDateTime,
  dateFormat = DATE_DISPLAY_FORMAT
) {
  return format(fulfillmentDateTime, dateFormat);
}

export function getFulfillmentTimeKey(fulfillmentDate) {
  return format(fulfillmentDate, TIME_KEY_FORMAT);
}

export function getFulfillmentTimeDisplay(fulfillmentDateTime) {
  return format(fulfillmentDateTime, TIME_DISPLAY_FORMAT);
}
